import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resolveDocTitle } from '../../utils/evaluationPlanDocTitle';
import AutoStatusToast from '../common/AutoStatusToast';
import ProgramSlideCanvas from '../program/ProgramSlideCanvas';
import EvaluationPlanSheetTableCreateModal from './EvaluationPlanSheetTableCreateModal';
import EvaluationPlanSheetTableEditMiniModal from './EvaluationPlanSheetTableEditMiniModal';
import type { EvaluationPlanAttachment, EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import { EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS } from '../../types/evaluationPlan';
import type { ProgramSlideLayoutV1, ProgramSlideTableBlockV1 } from '../../types/programContentSlideCanvas';
import {
  cloneEvaluationPlanSheetCanvasDocument,
  createDefaultEvaluationPlanSheetCanvasDocument,
  EVALUATION_PLAN_SHEET_CANVAS_TITLE,
  EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX,
  EVALUATION_PLAN_SHEET_FLOATING_UI_Z,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM,
  EVALUATION_PLAN_SHEET_MAX_PAGES,
  createEmptySheetLayoutSubset,
  getSheetCanvasPages,
  withSheetCanvasPages,
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
  type EvaluationPlanSheetCanvasDocumentV1,
  type EvaluationPlanSheetImageScope,
} from '../../types/evaluationPlanSheetCanvas';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import { getSheetCanvasFromAttachment } from '../../utils/evaluationPlanAttachment';
import {
  clearEvaluationPlanSheetCanvasDraft,
  loadEvaluationPlanSheetCanvasDraft,
  saveEvaluationPlanSheetCanvasDraft,
} from '../../utils/evaluationPlanSheetCanvasDraftStorage';
import UnsavedChangesConfirmModal from '../common/UnsavedChangesConfirmModal';
import EvaluationPlanDeleteHoldButton from './EvaluationPlanDeleteHoldButton';
import { createTableBlock, normalizeTableGrid, type TableSelectionRect } from '../../utils/programSlideTableBlock';
import {
  holdModalSavingLabelMinDuration,
  paintModalSaveInProgress,
} from '../../utils/modalSaveButtonIndicator';

const EvaluationPlanSheetCanvasEditorModal: React.FC<{
  open: boolean;
  slot: EvaluationPlanAttachmentSlot | null;
  existingAttachment?: EvaluationPlanAttachment | null;
  draftStorageKey?: string;
  imageScope?: EvaluationPlanSheetImageScope;
  workspaceId?: string | null;
  /** 평가계획 성취기준의 과목명(제목 자동 표시용) */
  subjectNames?: string[];
  onClose: () => void;
  onSave: (slot: EvaluationPlanAttachmentSlot, attachment: EvaluationPlanAttachment) => void | Promise<void>;
}> = ({
  open,
  slot,
  existingAttachment = null,
  draftStorageKey = '',
  imageScope = 'admin',
  workspaceId = null,
  subjectNames,
  onClose,
  onSave,
}) => {
  const [documentDraft, setDocumentDraft] = useState<EvaluationPlanSheetCanvasDocumentV1 | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [leftToolbarHost, setLeftToolbarHost] = useState<HTMLElement | null>(null);
  const [rightToolbarHost, setRightToolbarHost] = useState<HTMLElement | null>(null);
  const [tableCreateOpen, setTableCreateOpen] = useState(false);
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [tableSelection, setTableSelection] = useState<TableSelectionRect | null>(null);
  const [tableAnchorRect, setTableAnchorRect] = useState<DOMRect | null>(null);
  const [tableEditOpen, setTableEditOpen] = useState(true);
  const [focusTableId, setFocusTableId] = useState<string | null>(null);
  const [canvasScrollEl, setCanvasScrollEl] = useState<HTMLElement | null>(null);
  const [sheetModalEl, setSheetModalEl] = useState<HTMLElement | null>(null);
  const [sheetModalBounds, setSheetModalBounds] = useState<DOMRect | null>(null);
  const lastSheetModalBoundsKeyRef = useRef('');
  const previousLayoutRef = useRef<ProgramSlideLayoutV1 | null>(null);
  const legacyNoticeShownRef = useRef(false);
  const documentDraftRef = useRef<EvaluationPlanSheetCanvasDocumentV1 | null>(null);
  const hasEditedRef = useRef(false);
  const draftSaveTimerRef = useRef<number | null>(null);
  const wasOpenRef = useRef(false);
  const subjectNamesRef = useRef(subjectNames);
  subjectNamesRef.current = subjectNames;
  /** 기본/자동 제목이면 과목 이름을 넣은 제목으로 */
  const withAutoTitle = (doc: EvaluationPlanSheetCanvasDocumentV1): EvaluationPlanSheetCanvasDocumentV1 => ({
    ...doc,
    title: resolveDocTitle('sheet', doc.title, subjectNamesRef.current),
  });
  /** 현재 편집 중인 페이지(0 = 1쪽) */
  const [activePage, setActivePage] = useState(0);
  /** 페이지 추가·삭제·이동 때마다 증가 → 캔버스 재마운트(페이지별 실행 취소 기록 분리) */
  const [pageNonce, setPageNonce] = useState(0);
  const { toastState, showSuccess } = useAutoStatusToast(4000);

  documentDraftRef.current = documentDraft;

  const syncSheetModalBounds = useCallback(() => {
    if (!sheetModalEl) return;
    const rect = sheetModalEl.getBoundingClientRect();
    const key = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
    if (key === lastSheetModalBoundsKeyRef.current) return;
    lastSheetModalBoundsKeyRef.current = key;
    setSheetModalBounds(rect);
  }, [sheetModalEl]);

  useLayoutEffect(() => {
    syncSheetModalBounds();
  }, [syncSheetModalBounds]);

  useEffect(() => {
    if (!sheetModalEl) return;
    const sync = () => syncSheetModalBounds();
    window.addEventListener('resize', sync);
    canvasScrollEl?.addEventListener('scroll', sync, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sync);
      ro.observe(sheetModalEl);
    }
    return () => {
      window.removeEventListener('resize', sync);
      canvasScrollEl?.removeEventListener('scroll', sync);
      ro?.disconnect();
    };
  }, [canvasScrollEl, sheetModalEl, syncSheetModalBounds]);

  useEffect(() => {
    if (!open || slot !== 'sheet') {
      if (!open) {
        wasOpenRef.current = false;
        setSaving(false);
        setIsDirty(false);
        setSaveCompleted(false);
        setDocumentDraft(null);
        setActivePage(0);
        previousLayoutRef.current = null;
        legacyNoticeShownRef.current = false;
        hasEditedRef.current = false;
        setCloseConfirmOpen(false);
      }
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    hasEditedRef.current = false;
    setIsDirty(false);
    setSaveCompleted(false);
    setActivePage(0);
    setPageNonce((n) => n + 1);

    const localDraft = draftStorageKey ? loadEvaluationPlanSheetCanvasDraft(draftStorageKey) : null;
    if (localDraft) {
      hasEditedRef.current = true;
      setIsDirty(true);
      setDocumentDraft(withAutoTitle(cloneEvaluationPlanSheetCanvasDocument(localDraft)));
      previousLayoutRef.current = sheetLayoutSubsetToSlideLayout(localDraft.layout);
      return;
    }

    const parsedSheet = getSheetCanvasFromAttachment(existingAttachment);
    if (parsedSheet) {
      setDocumentDraft(withAutoTitle(cloneEvaluationPlanSheetCanvasDocument(parsedSheet)));
      previousLayoutRef.current = sheetLayoutSubsetToSlideLayout(parsedSheet.layout);
      return;
    }

    const legacyTable = existingAttachment?.form_data?.web_document;
    if (legacyTable != null && !legacyNoticeShownRef.current) {
      legacyNoticeShownRef.current = true;
      showSuccess('기존 테이블형 평가지는 A4 캔버스로 새로 작성합니다.');
    }

    const fresh = createDefaultEvaluationPlanSheetCanvasDocument();
    setDocumentDraft(withAutoTitle(fresh));
    previousLayoutRef.current = sheetLayoutSubsetToSlideLayout(fresh.layout);
  }, [open, slot, existingAttachment, draftStorageKey, showSuccess]);

  useEffect(() => {
    return () => {
      if (draftSaveTimerRef.current != null) {
        window.clearTimeout(draftSaveTimerRef.current);
      }
      const doc = documentDraftRef.current;
      if (hasEditedRef.current && doc && draftStorageKey.trim()) {
        saveEvaluationPlanSheetCanvasDraft(draftStorageKey, doc);
      }
    };
  }, [draftStorageKey]);

  const scheduleDraftPersist = (doc: EvaluationPlanSheetCanvasDocumentV1) => {
    if (!draftStorageKey.trim()) return;
    if (draftSaveTimerRef.current != null) {
      window.clearTimeout(draftSaveTimerRef.current);
    }
    draftSaveTimerRef.current = window.setTimeout(() => {
      saveEvaluationPlanSheetCanvasDraft(draftStorageKey, doc);
      draftSaveTimerRef.current = null;
    }, 280);
  };

  const pages = useMemo(() => (documentDraft ? getSheetCanvasPages(documentDraft) : []), [documentDraft]);
  const pageIndex = Math.min(activePage, Math.max(0, pages.length - 1));
  const slideLayout = useMemo(
    () => (pages[pageIndex] ? sheetLayoutSubsetToSlideLayout(pages[pageIndex]) : null),
    [pages, pageIndex]
  );

  const activeTable = useMemo(() => {
    if (!slideLayout || !activeTableId) return null;
    return (slideLayout.tables ?? []).find((tbl) => tbl.id === activeTableId) ?? null;
  }, [activeTableId, slideLayout]);

  const handleTableSelectionChange = useCallback((tableId: string | null, selection: TableSelectionRect | null) => {
    setActiveTableId(tableId);
    setTableSelection(selection);
    if (tableId) setTableEditOpen(true);
  }, []);

  const handleTableAnchorRectChange = useCallback((rect: DOMRect | null) => {
    setTableAnchorRect((prev) => {
      if (!rect && !prev) return prev;
      if (!rect || !prev) return rect;
      if (
        Math.round(prev.top) === Math.round(rect.top) &&
        Math.round(prev.left) === Math.round(rect.left) &&
        Math.round(prev.width) === Math.round(rect.width) &&
        Math.round(prev.height) === Math.round(rect.height)
      ) {
        return prev;
      }
      return rect;
    });
  }, []);

  if (!open || slot !== 'sheet') return null;

  // 드래프트 준비 전에도 튜토리얼이 캔버스 진입을 감지할 수 있게 마커를 남긴다.
  if (!documentDraft || !slideLayout) {
    return (
      <div
        className="cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop cp-evaluation-plan-sheet-canvas-backdrop"
        data-tutorial-ep="sheet-canvas-modal"
        aria-hidden
      />
    );
  }

  const slotLabel = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
  /** 1쪽: 머리말 띠 아래부터 / 2쪽 이후: 머리말 없이 상단 여백만 */
  const pageTopNorm =
    pageIndex === 0
      ? documentDraft.headerBandNorm ?? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM
      : EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM;

  const updatePages = (nextPages: typeof pages, nextActive: number) => {
    hasEditedRef.current = true;
    setIsDirty(true);
    setSaveCompleted(false);
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setDocumentDraft((prev) => {
      if (!prev) return prev;
      const nextDoc = withSheetCanvasPages(prev, nextPages);
      scheduleDraftPersist(nextDoc);
      return nextDoc;
    });
    setActivePage(Math.max(0, Math.min(nextActive, nextPages.length - 1)));
    setPageNonce((n) => n + 1);
  };
  const goToPage = (index: number) => {
    if (index === pageIndex) return;
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setActivePage(index);
    setPageNonce((n) => n + 1);
  };
  const addPage = () => {
    if (pages.length >= EVALUATION_PLAN_SHEET_MAX_PAGES) return;
    const blank = createEmptySheetLayoutSubset();
    const base = pages[pageIndex] ?? pages[0];
    if (base) {
      blank.pageBackgroundColorHex = base.pageBackgroundColorHex;
      blank.defaultTextColorHex = base.defaultTextColorHex;
    }
    // 활동지와 같이 맨 끝에 추가하고 새 페이지로 이동
    updatePages([...pages, blank], pages.length);
  };
  const deletePage = (index: number) => {
    if (pages.length <= 1 || index < 0 || index >= pages.length) return;
    const nextActive = index < pageIndex ? pageIndex - 1 : index === pageIndex ? Math.max(0, index - 1) : pageIndex;
    updatePages(pages.filter((_, i) => i !== index), nextActive);
  };


  const handleSheetTitleChange = (nextTitle: string) => {
    hasEditedRef.current = true;
    setIsDirty(true);
    setSaveCompleted(false);
    setDocumentDraft((prev) => {
      if (!prev) return prev;
      const nextDoc = { ...prev, title: nextTitle };
      scheduleDraftPersist(nextDoc);
      return nextDoc;
    });
  };

  const handleLayoutChange = (next: ProgramSlideLayoutV1) => {
    hasEditedRef.current = true;
    setIsDirty(true);
    setSaveCompleted(false);
    setDocumentDraft((prev) => {
      const nextDoc = prev
        ? (() => {
            const list = getSheetCanvasPages(prev).slice();
            list[pageIndex] = slideLayoutToSheetLayoutSubset(next);
            return withSheetCanvasPages(prev, list);
          })()
        : prev;
      if (nextDoc) scheduleDraftPersist(nextDoc);
      return nextDoc;
    });
  };

  const patchTableInLayout = (table: ProgramSlideTableBlockV1, selection?: TableSelectionRect | null) => {
    const normalized = normalizeTableGrid(table);
    handleLayoutChange({
      ...slideLayout,
      tables: (slideLayout.tables ?? []).map((tbl) => (tbl.id === normalized.id ? normalized : tbl)),
    });
    if (selection) {
      setTableSelection(selection);
    }
  };

  const handleCreateTable = (input: {
    rowCount: number;
    colCount: number;
  }) => {
    const top = pageTopNorm;
    const centerY = (top + 1) / 2;
    const zs = [
      ...slideLayout.boxes.map((b) => b.zIndex),
      ...(slideLayout.tables ?? []).map((t) => t.zIndex),
      ...(slideLayout.shapes ?? []).map((s) => s.zIndex),
    ];
    const zIndex = zs.length ? Math.max(...zs) + 1 : 0;
    const table = createTableBlock({
      rowCount: input.rowCount,
      colCount: input.colCount,
      defaultFontSizePx: EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX,
      defaultTextColorHex: slideLayout.defaultTextColorHex ?? '#111111',
      x: 0.1,
      y: Math.min(0.72, Math.max(top + 0.02, centerY - 0.18)),
      w: 0.8,
      h: 0.35,
      zIndex,
    });
    handleLayoutChange({
      ...slideLayout,
      tables: [...(slideLayout.tables ?? []), table],
    });
    setTableCreateOpen(false);
    setActiveTableId(table.id);
    setTableEditOpen(true);
    setFocusTableId(table.id);
  };

  const handleDeleteTable = () => {
    if (!activeTableId) return;
    handleLayoutChange({
      ...slideLayout,
      tables: (slideLayout.tables ?? []).filter((tbl) => tbl.id !== activeTableId),
    });
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
  };

  const handleSave = async (): Promise<boolean> => {
    if (saving || !isDirty) return !isDirty;
    let ok = false;
    const saveStartedAt = Date.now();
    setSaving(true);
    await paintModalSaveInProgress();
    try {
      // 이미지 업로드·삭제는 하지 않음: 새 이미지는 로컬(blob)로 평가계획 폼에 담기고,
      // 평가계획 [저장] 시 업로드 / 저장 성공 후 빠진 이미지 정리(evaluationSheetImageLifecycle)
      const finalizedLayout = slideLayout;
      const nextDocument: EvaluationPlanSheetCanvasDocumentV1 = documentDraft;
      await onSave(slot, {
        kind: 'web_document',
        slot,
        registered_at: existingAttachment?.registered_at ?? new Date().toISOString(),
        preview_label: nextDocument.title.trim() || EVALUATION_PLAN_SHEET_CANVAS_TITLE,
        form_data: { sheet_canvas: nextDocument },
      });
      if (draftStorageKey.trim()) {
        clearEvaluationPlanSheetCanvasDraft(draftStorageKey);
      }
      previousLayoutRef.current = finalizedLayout;
      setDocumentDraft(cloneEvaluationPlanSheetCanvasDocument(nextDocument));
      hasEditedRef.current = false;
      setIsDirty(false);
      setSaveCompleted(true);
      ok = true;
    } catch (error) {
      console.error('평가지 캔버스 저장 실패:', error);
      alert('평가지 등록에 실패했습니다.');
    } finally {
      await holdModalSavingLabelMinDuration(saveStartedAt);
      setSaving(false);
    }
    return ok;
  };

  /** 저장 안 함: 로컬 초안·편집 표시 버리고 닫기 */
  const discardAndClose = () => {
    if (draftSaveTimerRef.current != null) {
      window.clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    hasEditedRef.current = false;
    if (draftStorageKey.trim()) clearEvaluationPlanSheetCanvasDraft(draftStorageKey);
    setIsDirty(false);
    onClose();
  };
  const requestClose = () => {
    if (saving) return;
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <div
        className="cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop cp-evaluation-plan-sheet-canvas-backdrop"
        role="presentation"
        onKeyDown={(e) => {
          if (e.key === 'Escape') requestClose();
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) requestClose();
        }}
      >
        <div
          ref={setSheetModalEl}
          className="cp-modal cp-evaluation-plan-web-doc-modal cp-evaluation-plan-sheet-canvas-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-evaluation-plan-sheet-canvas-editor-title"
          data-tutorial-ep="sheet-canvas-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cp-evaluation-plan-web-doc-modal-head">
            <div className="cp-evaluation-plan-web-doc-modal-head-row">
              <h3 id="cp-evaluation-plan-sheet-canvas-editor-title">{slotLabel} A4 캔버스 작성</h3>
            </div>
          </div>

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-body cp-evaluation-plan-sheet-canvas-body">
            <div className="cp-program-slide-root cp-program-slide-root--sheet-editor" data-slide-surface="light">
              <div className="cp-evaluation-plan-sheet-canvas-workspace">
                <div
                  ref={setLeftToolbarHost}
                  className="cp-evaluation-plan-sheet-canvas-toolbar-host cp-evaluation-plan-sheet-canvas-toolbar-host--left"
                  data-tutorial-ep="sheet-toolbar-left"
                  aria-hidden={!leftToolbarHost}
                />
                <div ref={setCanvasScrollEl} className="cp-evaluation-plan-sheet-canvas-scroll">
                  <div className="cp-learning-worksheet-page-stack">
                    {pages.map((pageSubset, i) => {
                      const isActive = i === pageIndex;
                      const topNorm =
                        i === 0
                          ? documentDraft.headerBandNorm ?? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM
                          : EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM;
                      const inactiveLayout = isActive ? null : sheetLayoutSubsetToSlideLayout(pageSubset);
                      return (
                        <div key={`sheet-page-${i}`} className={`cp-learning-worksheet-page-frame${isActive ? ' is-active' : ''}`}>
                          <div className="cp-learning-worksheet-page-main">
                            {!isActive ? (
                              <button
                                type="button"
                                className="cp-learning-worksheet-page-focus-hit"
                                onClick={() => goToPage(i)}
                                aria-label={`${i + 1}페이지 선택`}
                              />
                            ) : null}
                            <div className="cp-evaluation-plan-sheet-canvas-a4-page">
                              {isActive ? (
                          <ProgramSlideCanvas
                            key={`sheet-page-${pageNonce}`}
                            layout={slideLayout}
                            onLayoutChange={handleLayoutChange}
                            readOnly={false}
                            toolbarPreset="evaluation-sheet"
                            canvasAspectRatio="a4"
                            imageStorageKind="evaluation-sheet"
                            evaluationSheetImageScope={imageScope}
                            evaluationSheetWorkspaceId={workspaceId}
                            externalToolbarLeftHost={leftToolbarHost}
                            externalToolbarRightHost={rightToolbarHost}
                            positionSyncRoot={canvasScrollEl}
                            viewportContainerRect={sheetModalBounds}
                            fixedHeaderBand={
                              pageIndex === 0
                                ? {
                                    title: documentDraft.title,
                                    academicYearLabel: documentDraft.academicYearLabel,
                                    titleEditable: true,
                                    onTitleChange: handleSheetTitleChange,
                                  }
                                : undefined
                            }
                            contentBoundsNorm={{ top: pageTopNorm, bottom: 1 }}
                            defaultTextColorHex={slideLayout.defaultTextColorHex}
                            canvasBackgroundColorHex={slideLayout.pageBackgroundColorHex}
                            onRequestCreateTable={() => setTableCreateOpen(true)}
                            onTableSelectionChange={handleTableSelectionChange}
                            onTableAnchorRectChange={handleTableAnchorRectChange}
                            focusTableId={focusTableId}
                            onFocusTableHandled={() => setFocusTableId(null)}
                            syncedTableCellSelection={
                              activeTableId && tableSelection
                                ? { tableId: activeTableId, ...tableSelection }
                                : null
                            }
                          />
                              ) : (
                                <ProgramSlideCanvas
                                  layout={inactiveLayout!}
                                  onLayoutChange={() => {}}
                                  readOnly
                                  toolbarPreset="evaluation-sheet"
                                  canvasAspectRatio="a4"
                                  imageStorageKind="evaluation-sheet"
                                  evaluationSheetImageScope={imageScope}
                                  evaluationSheetWorkspaceId={workspaceId}
                                  fixedHeaderBand={
                                    i === 0
                                      ? { title: documentDraft.title, academicYearLabel: documentDraft.academicYearLabel }
                                      : undefined
                                  }
                                  contentBoundsNorm={{ top: topNorm, bottom: 1 }}
                                  defaultTextColorHex={inactiveLayout!.defaultTextColorHex}
                                  canvasBackgroundColorHex={inactiveLayout!.pageBackgroundColorHex}
                                />
                              )}
                            </div>
                          </div>
                          {pages.length > 1 ? (
                            <div className="cp-learning-worksheet-page-actions">
                              <EvaluationPlanDeleteHoldButton
                                itemId={`sheet-page-${i}-${pageNonce}`}
                                className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-delete"
                                label="페이지 삭제"
                                onRemove={() => deletePage(i)}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-add"
                      onClick={addPage}
                      disabled={pages.length >= EVALUATION_PLAN_SHEET_MAX_PAGES}
                      aria-label="페이지 추가"
                      title={
                        pages.length >= EVALUATION_PLAN_SHEET_MAX_PAGES
                          ? `최대 ${EVALUATION_PLAN_SHEET_MAX_PAGES}페이지까지 추가할 수 있습니다.`
                          : '페이지 추가'
                      }
                    >
                      <span className="cp-learning-worksheet-page-add-icon" aria-hidden>+</span>
                      <span className="cp-learning-worksheet-page-add-label">페이지 추가</span>
                    </button>
                  </div>
                </div>
                <div
                  ref={setRightToolbarHost}
                  className="cp-evaluation-plan-sheet-canvas-toolbar-host cp-evaluation-plan-sheet-canvas-toolbar-host--right"
                  data-tutorial-ep="sheet-toolbar-right"
                  aria-hidden={!rightToolbarHost}
                />
              </div>
            </div>
          </div>

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-footer cp-evaluation-plan-web-doc-modal-footer">
            <button
              type="button"
              className="cp-btn-secondary"
              data-tutorial-ep="sheet-cancel"
              onClick={requestClose}
              disabled={saving}
            >
              닫기
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              data-tutorial-ep="sheet-save"
              onClick={() => void handleSave()}
              disabled={saving || !isDirty}
              title={!isDirty && !saveCompleted ? '변경 사항이 없습니다' : undefined}
            >
              {saving ? '등록 중…' : saveCompleted && !isDirty ? '등록 완료' : '등록'}
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesConfirmModal
        open={closeConfirmOpen}
        primaryLabel="등록 하기"
        primaryDisabled={saving}
        onCancel={() => setCloseConfirmOpen(false)}
        onDiscard={() => {
          setCloseConfirmOpen(false);
          discardAndClose();
        }}
        onPrimary={() => {
          setCloseConfirmOpen(false);
          void handleSave().then((ok) => {
            if (ok) onClose();
          });
        }}
      />
      <EvaluationPlanSheetTableCreateModal
        open={tableCreateOpen}
        onClose={() => setTableCreateOpen(false)}
        onSubmit={handleCreateTable}
      />
      <EvaluationPlanSheetTableEditMiniModal
        open={!!activeTableId && !!activeTable && tableEditOpen}
        table={activeTable}
        selection={tableSelection}
        anchorRect={tableAnchorRect}
        containerRect={sheetModalBounds}
        panelZIndex={EVALUATION_PLAN_SHEET_FLOATING_UI_Z}
        onClose={() => setTableEditOpen(false)}
        onPatchTable={patchTableInLayout}
        onDeleteTable={handleDeleteTable}
        onToast={showSuccess}
      />
      <AutoStatusToast state={toastState} />
    </>
  );
};

export default EvaluationPlanSheetCanvasEditorModal;
